import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Clock, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevR, Loader2, CircleDot, Download, AlertTriangle } from "lucide-react";

const fmtTime = (s) => (s ? new Date(s).toLocaleString("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit" }) : "");
const weekLabel = (ws, off) => {
  if (!ws) return "";
  const start = new Date(ws);
  const end = new Date(start); end.setDate(end.getDate() + 6);
  const f = (d) => d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  return `${f(start)} – ${f(end)}${off === 0 ? " (this week)" : ""}`;
};

export default function TimesheetsPage() {
  const [data, setData] = useState({ rows: [], week_start: null, week_offset: 0 });
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });

  const load = (off) => { setLoading(true); api.get(`/timesheets?week_offset=${off}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(offset); }, [offset]);

  const totalAll = data.rows.reduce((a, r) => a + r.total_hours, 0);
  const onClock = data.rows.filter((r) => r.open).length;
  const overCount = data.rows.filter((r) => Number(r.weekly_hours) && r.total_hours > Number(r.weekly_hours)).length;

  const exportCsv = async () => {
    try {
      const r = await api.get(`/timesheets/export?week_offset=${offset}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `timesheets_${(data.week_start || "").slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const exportRange = async () => {
    if (!range.from || !range.to) { toast.error("Pick a start and end date"); return; }
    if (range.to < range.from) { toast.error("End date cannot be before start date"); return; }
    try {
      const r = await api.get(`/timesheets/export?from_date=${range.from}&to_date=${range.to}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `timesheets_${range.from}_to_${range.to}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Payroll CSV exported");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="timesheets-page">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-3"><PageIcon /> Staff Timesheets</h1>
          <p className="text-sm text-slate-500 mt-1">Weekly hours and check-ins for every member of staff who clocks in.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={exportCsv} data-testid="timesheet-export"><Download className="w-4 h-4" /> Export CSV</Button>
          <button onClick={() => setOffset((o) => o - 1)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="timesheet-prev-week"><ChevronLeft className="w-4 h-4" /></button>
          <div className="text-sm font-semibold text-slate-700 min-w-[190px] text-center" data-testid="timesheet-week-label">{weekLabel(data.week_start, data.week_offset)}</div>
          <button onClick={() => setOffset((o) => Math.min(0, o + 1))} disabled={offset >= 0} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-30" data-testid="timesheet-next-week"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Stat label="Staff tracked" value={data.rows.length} />
        <Stat label="Total hours" value={`${totalAll.toFixed(1)}h`} />
        <Stat label="On the clock now" value={onClock} accent={onClock > 0} />
        <Stat label="Over target" value={overCount} accent={overCount > 0} warn />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="payroll-range">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 mr-1">Payroll export</span>
        <input type="date" value={range.from} onChange={(e) => setRange((s) => ({ ...s, from: e.target.value }))} className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm" data-testid="payroll-from" />
        <span className="text-slate-400 text-sm">to</span>
        <input type="date" value={range.to} onChange={(e) => setRange((s) => ({ ...s, to: e.target.value }))} className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm" data-testid="payroll-to" />
        <Button variant="outline" size="sm" className="gap-1.5" onClick={exportRange} disabled={!range.from || !range.to} data-testid="payroll-export"><Download className="w-4 h-4" /> Export range CSV</Button>
        <span className="text-[11px] text-slate-400 ml-auto">Export a full month or any custom range for payroll</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {loading ? <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          : data.rows.length === 0 ? <div className="p-8 text-center text-sm text-slate-400">No staff clock in for this week.</div>
          : data.rows.map((r) => {
            const target = Number(r.weekly_hours) || 0;
            const pct = target ? Math.min(100, (r.total_hours / target) * 100) : 0;
            const isOpen = open === r.user_id;
            return (
              <div key={r.user_id} className="border-b border-slate-100 last:border-0" data-testid={`timesheet-row-${r.email}`}>
                <button onClick={() => setOpen(isOpen ? null : r.user_id)} className="w-full flex items-center gap-4 px-4 py-3 hover:bg-slate-50 text-left">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevR className="w-4 h-4 text-slate-400 shrink-0" />}
                  <div className="w-48 min-w-0">
                    <div className="font-medium text-slate-800 truncate">{r.name}</div>
                    <div className="text-[11px] text-slate-400 truncate">{(r.roles || []).join(", ")}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-semibold text-slate-700">{r.total_hours.toFixed(1)}h{target ? <span className="text-slate-400"> / {target}h</span> : null}</span>
                      <span className="text-slate-400">{r.count} session{r.count === 1 ? "" : "s"}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full ${pct >= 100 ? "bg-emerald-500" : "bg-indigo-500"}`} style={{ width: `${target ? pct : 0}%` }} />
                    </div>
                  </div>
                  {r.open && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full shrink-0" data-testid={`timesheet-onclock-${r.email}`}><CircleDot className="w-3 h-3" /> On the clock</span>}
                  {target > 0 && (r.total_hours > target
                    ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full shrink-0" data-testid={`timesheet-over-${r.email}`}><AlertTriangle className="w-3 h-3" /> Over by {(r.total_hours - target).toFixed(1)}h</span>
                    : pct >= 100
                      ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full shrink-0">On target</span>
                      : <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full shrink-0" data-testid={`timesheet-under-${r.email}`}>{(target - r.total_hours).toFixed(1)}h under</span>)}
                </button>
                {isOpen && (
                  <div className="px-5 pb-4 bg-slate-50/50">
                    {r.sessions.length === 0 ? <div className="text-sm text-slate-400 py-2">No check-ins this week.</div> : (
                      <div className="space-y-1.5 pt-1">
                        {r.sessions.map((s, i) => (
                          <div key={i} className="flex items-center justify-between text-sm rounded-lg border border-slate-100 bg-white px-3 py-2">
                            <span className="text-slate-600">{fmtTime(s.start)} → {s.end ? fmtTime(s.end) : <span className="text-emerald-600">in progress</span>}{s.auto_closed && <span className="ml-2 text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">auto-closed</span>}</span>
                            <span className="font-medium text-slate-800">{s.hours.toFixed(2)}h</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, warn }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-2xl font-bold ${accent ? (warn ? "text-rose-600" : "text-emerald-600") : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
